import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  FontFamily,
  TypographyScale,
  TypographyConfig,
  TextStyle,
  TypographyConfigCreateRequest,
  TextStyleUpdateRequest,
  TypographyApiResponse,
  FontVariantsResponse,
  FontVariant
} from './typography.interface';

@Injectable({
  providedIn: 'root'
})
export class TypographyRepository {
  private readonly baseUrl = `${environment.apiUrlHost}/AD`;

  constructor(private http: HttpClient) {}

  /**
   * Get all font families
   */
  getFontFamilies(): Observable<FontFamily[]> {
    console.log('📡 TypographyRepository: Fetching font families...');
    return this.http.get<FontFamily[]>(`${this.baseUrl}/font-families/`).pipe(
      map(response => {
        console.log('✅ TypographyRepository: Font families loaded:', response);
        return response;
      }),
      catchError(this.handleError('getFontFamilies', []))
    );
  }

  /**
   * Get font family by ID
   */
  getFontFamily(id: number): Observable<FontFamily> {
    console.log(`📡 TypographyRepository: Fetching font family ${id}...`);
    return this.http.get<FontFamily>(`${this.baseUrl}/font-families/${id}/`).pipe(
      map(response => {
        console.log(`✅ TypographyRepository: Font family ${id} loaded:`, response);
        return response;
      }),
      catchError(this.handleError('getFontFamily', null))
    );
  }

  /**
   * Create new font family
   */
  createFontFamily(fontFamily: Partial<FontFamily>): Observable<FontFamily> {
    console.log('TypographyRepository: Creating font family...', fontFamily);
    return this.http.post<FontFamily>(`${this.baseUrl}/font-families/`, fontFamily).pipe(
      map(response => {
        console.log('✅ TypographyRepository: Font family created:', response);
        return response;
      }),
      catchError(this.handleError('createFontFamily', null))
    );
  }

  /**
   * Create new font family with file upload (for custom fonts)
   */
  createFontFamilyWithFile(formData: FormData): Observable<FontFamily> {
    console.log('📡 TypographyRepository: Creating font family with file upload...');
    return this.http.post<FontFamily>(`${this.baseUrl}/font-families/`, formData).pipe(
      map(response => {
        console.log('✅ TypographyRepository: Font family created with file:', response);
        return response;
      }),
      catchError(this.handleError('createFontFamilyWithFile', null))
    );
  }

  /**
   * Update font family
   */
  updateFontFamily(id: number, fontFamily: Partial<FontFamily>): Observable<FontFamily> {
    console.log(`📡 TypographyRepository: Updating font family ${id}...`, fontFamily);
    return this.http.put<FontFamily>(`${this.baseUrl}/font-families/${id}/`, fontFamily).pipe(
      map(response => {
        console.log(`✅ TypographyRepository: Font family ${id} updated:`, response);
        return response;
      }),
      catchError(this.handleError('updateFontFamily', null))
    );
  }

  /**
   * Delete font family
   */
  deleteFontFamily(id: number): Observable<void> {
    console.log(`📡 TypographyRepository: Deleting font family ${id}...`);
    return this.http.delete<void>(`${this.baseUrl}/font-families/${id}/`).pipe(
      map(() => {
        console.log(`✅ TypographyRepository: Font family ${id} deleted`);
      }),
      catchError(this.handleError('deleteFontFamily', null))
    );
  }

  /**
   * Get all typography scales
   */
  getTypographyScales(): Observable<TypographyScale[]> {
    console.log('📡 TypographyRepository: Fetching typography scales...');
    return this.http.get<TypographyScale[]>(`${this.baseUrl}/typography-scales/`).pipe(
      map(response => {
        console.log('✅ TypographyRepository: Typography scales loaded:', response);
        return response;
      }),
      catchError(this.handleError('getTypographyScales', []))
    );
  }

  /**
   * Get typography scale by ID
   */
  getTypographyScale(id: number): Observable<TypographyScale> {
    console.log(`📡 TypographyRepository: Fetching typography scale ${id}...`);
    return this.http.get<TypographyScale>(`${this.baseUrl}/typography-scales/${id}/`).pipe(
      map(response => {
        console.log(`✅ TypographyRepository: Typography scale ${id} loaded:`, response);
        return response;
      }),
      catchError(this.handleError('getTypographyScale', null))
    );
  }

  /**
   * Create new typography scale
   */
  createTypographyScale(scale: Partial<TypographyScale>): Observable<TypographyScale> {
    console.log('📡 TypographyRepository: Creating typography scale...', scale);
    return this.http.post<TypographyScale>(`${this.baseUrl}/typography-scales/`, scale).pipe(
      map(response => {
        console.log('✅ TypographyRepository: Typography scale created:', response);
        return response;
      }),
      catchError(this.handleError('createTypographyScale', null))
    );
  }

  /**
   * Update typography scale
   */
  updateTypographyScale(id: number, scale: Partial<TypographyScale>): Observable<TypographyScale> {
    console.log(`📡 TypographyRepository: Updating typography scale ${id}...`, scale);
    return this.http.put<TypographyScale>(`${this.baseUrl}/typography-scales/${id}/`, scale).pipe(
      map(response => {
        console.log(`✅ TypographyRepository: Typography scale ${id} updated:`, response);
        return response;
      }),
      catchError(this.handleError('updateTypographyScale', null))
    );
  }

  /**
   * Delete typography scale
   */
  deleteTypographyScale(id: number): Observable<void> {
    console.log(`📡 TypographyRepository: Deleting typography scale ${id}...`);
    return this.http.delete<void>(`${this.baseUrl}/typography-scales/${id}/`).pipe(
      map(() => {
        console.log(`✅ TypographyRepository: Typography scale ${id} deleted`);
      }),
      catchError(this.handleError('deleteTypographyScale', null))
    );
  }

  /**
   * Get all typography configurations
   */
  getTypographyConfigs(): Observable<TypographyConfig[]> {
    console.log('📡 TypographyRepository: Fetching typography configs...');
    return this.http.get<TypographyConfig[]>(`${this.baseUrl}/typography-configs/`).pipe(
      map(response => {
        console.log('✅ TypographyRepository: Typography configs loaded:', response);
        return response;
      }),
      catchError(this.handleError('getTypographyConfigs', []))
    );
  }

  /**
   * Get typography configuration by ID
   */
  getTypographyConfig(id: number): Observable<TypographyConfig> {
    console.log(`📡 TypographyRepository: Fetching typography config ${id}...`);
    return this.http.get<TypographyConfig>(`${this.baseUrl}/typography-configs/${id}/`).pipe(
      map(response => {
        console.log(`✅ TypographyRepository: Typography config ${id} loaded:`, response);
        return response;
      }),
      catchError(this.handleError('getTypographyConfig', null))
    );
  }

  /**
   * Get active typography configuration
   * Note: API returns an array [{...}], so we extract the active one (is_active: true)
   */
  getActiveTypographyConfig(): Observable<TypographyConfig> {
    console.log('📡 TypographyRepository: Fetching active typography config...');
    return this.http.get<TypographyConfig | TypographyConfig[]>(`${this.baseUrl}/typography-configs/active/`).pipe(
      map(response => {
        // Handle array response - find the one with is_active: true
        if (Array.isArray(response)) {
          const activeConfig = response.find(config => config.is_active === true);
          if (activeConfig) {
            console.log('✅ TypographyRepository: Active typography config found:', activeConfig);
            return activeConfig;
          }
          // Fallback to first element if no active found
          console.warn('⚠️ TypographyRepository: No active config found, using first element');
          return response[0];
        }
        console.log('✅ TypographyRepository: Active typography config loaded:', response);
        return response;
      }),
      catchError(this.handleError('getActiveTypographyConfig', null))
    );
  }

  /**
   * Get all typography configurations (returns array)
   * Used to extract all fonts from all configs
   */
  getAllTypographyConfigs(): Observable<TypographyConfig[]> {
    console.log('📡 TypographyRepository: Fetching all typography configs...');
    return this.http.get<TypographyConfig[]>(`${this.baseUrl}/typography-configs/active/`).pipe(
      map(response => {
        const configs = Array.isArray(response) ? response : [response];
        console.log('✅ TypographyRepository: All typography configs loaded:', configs.length);
        return configs;
      }),
      catchError(this.handleError('getAllTypographyConfigs', []))
    );
  }

  /**
   * Create new typography configuration
   */
  createTypographyConfig(config: TypographyConfigCreateRequest): Observable<TypographyConfig> {
    console.log('📡 TypographyRepository: Creating typography config...', config);
    return this.http.post<TypographyConfig>(`${this.baseUrl}/typography-configs/`, config).pipe(
      map(response => {
        console.log('✅ TypographyRepository: Typography config created:', response);
        return response;
      }),
      catchError(this.handleError('createTypographyConfig', null))
    );
  }

  /**
   * Update typography configuration
   */
  updateTypographyConfig(id: number, config: Partial<TypographyConfigCreateRequest>): Observable<TypographyConfig> {
    console.log(`📡 TypographyRepository: Updating typography config ${id}...`, config);
    return this.http.put<TypographyConfig>(`${this.baseUrl}/typography-configs/${id}/`, config).pipe(
      map(response => {
        console.log(`✅ TypographyRepository: Typography config ${id} updated:`, response);
        return response;
      }),
      catchError(this.handleError('updateTypographyConfig', null))
    );
  }

  /**
   * Delete typography configuration
   */
  deleteTypographyConfig(id: number): Observable<void> {
    console.log(`📡 TypographyRepository: Deleting typography config ${id}...`);
    return this.http.delete<void>(`${this.baseUrl}/typography-configs/${id}/`).pipe(
      map(() => {
        console.log(`✅ TypographyRepository: Typography config ${id} deleted`);
      }),
      catchError(this.handleError('deleteTypographyConfig', null))
    );
  }

  /**
   * Activate typography configuration
   */
  activateTypographyConfig(id: number): Observable<TypographyConfig> {
    console.log(`📡 TypographyRepository: Activating typography config ${id}...`);
    return this.http.post<TypographyConfig>(`${this.baseUrl}/typography-configs/${id}/activate/`, {}).pipe(
      map(response => {
        console.log(`✅ TypographyRepository: Typography config ${id} activated:`, response);
        return response;
      }),
      catchError(this.handleError('activateTypographyConfig', null))
    );
  }

  /**
   * Get all text styles
   */
  getTextStyles(): Observable<TextStyle[]> {
    console.log('📡 TypographyRepository: Fetching text styles...');
    return this.http.get<TextStyle[]>(`${this.baseUrl}/text-styles/`).pipe(
      map(response => {
        console.log('✅ TypographyRepository: Text styles loaded:', response);
        return response;
      }),
      catchError(this.handleError('getTextStyles', []))
    );
  }

  /**
   * Get text style by ID
   */
  getTextStyle(id: number): Observable<TextStyle> {
    console.log(`📡 TypographyRepository: Fetching text style ${id}...`);
    return this.http.get<TextStyle>(`${this.baseUrl}/text-styles/${id}/`).pipe(
      map(response => {
        console.log(`✅ TypographyRepository: Text style ${id} loaded:`, response);
        return response;
      }),
      catchError(this.handleError('getTextStyle', null))
    );
  }

  /**
   * Create new text style
   */
  createTextStyle(textStyle: Partial<TextStyle>): Observable<TextStyle> {
    console.log('📡 TypographyRepository: Creating text style...', textStyle);
    return this.http.post<TextStyle>(`${this.baseUrl}/text-styles/`, textStyle).pipe(
      map(response => {
        console.log('✅ TypographyRepository: Text style created:', response);
        return response;
      }),
      catchError(this.handleError('createTextStyle', null))
    );
  }

  /**
   * Update text style
   */
  updateTextStyle(id: number, textStyle: TextStyleUpdateRequest): Observable<TextStyle> {
    console.log(`📡 TypographyRepository: Updating text style ${id}...`, textStyle);
    return this.http.put<TextStyle>(`${this.baseUrl}/text-styles/${id}/`, textStyle).pipe(
      map(response => {
        console.log(`✅ TypographyRepository: Text style ${id} updated:`, response);
        return response;
      }),
      catchError(this.handleError('updateTextStyle', null))
    );
  }

  /**
   * Delete text style
   */
  deleteTextStyle(id: number): Observable<void> {
    console.log(`📡 TypographyRepository: Deleting text style ${id}...`);
    return this.http.delete<void>(`${this.baseUrl}/text-styles/${id}/`).pipe(
      map(() => {
        console.log(`✅ TypographyRepository: Text style ${id} deleted`);
      }),
      catchError(this.handleError('deleteTextStyle', null))
    );
  }

  /**
   * Bulk update text styles for a typography configuration
   */
  bulkUpdateTextStyles(configId: number, textStyles: TextStyleUpdateRequest[]): Observable<TextStyle[]> {
    console.log(`📡 TypographyRepository: Bulk updating text styles for config ${configId}...`, textStyles);
    return this.http.put<TextStyle[]>(`${this.baseUrl}/typography-configs/${configId}/bulk-update/`, {
      text_styles: textStyles
    }).pipe(
      map(response => {
        console.log(`✅ TypographyRepository: Text styles for config ${configId} bulk updated:`, response);
        return response;
      }),
      catchError(this.handleError('bulkUpdateTextStyles', []))
    );
  }

  /**
   * Get font variants for a specific font family
   * @param fontName Font family name (case-insensitive)
   * @param themeId Optional theme ID (defaults to active theme)
   */
  getFontVariants(fontName: string, themeId?: number): Observable<FontVariantsResponse> {
    console.log(`📡 TypographyRepository: Fetching font variants for "${fontName}"...`);
    const params = new URLSearchParams();
    if (themeId) {
      params.append('theme_id', themeId.toString());
    }
    params.append('font_name', fontName);
    
    return this.http.get<FontVariantsResponse>(`${this.baseUrl}/font-variants/?${params.toString()}`).pipe(
      map(response => {
        console.log(`✅ TypographyRepository: Font variants loaded for "${fontName}":`, response);
        return response;
      }),
      catchError(this.handleError('getFontVariants', null))
    );
  }

  /**
   * Activate a font variant (typography config)
   * @param variantId The typography config ID to activate
   */
  activateVariant(variantId: number): Observable<TypographyConfig> {
    console.log(`📡 TypographyRepository: Activating variant ${variantId}...`);
    return this.http.post<TypographyConfig>(`${this.baseUrl}/typography-configs/${variantId}/activate/`, {}).pipe(
      map(response => {
        console.log(`✅ TypographyRepository: Variant ${variantId} activated:`, response);
        return response;
      }),
      catchError(this.handleError('activateVariant', null))
    );
  }

  /**
   * Error handling
   */
  private handleError<T>(operation = 'operation', result?: T) {
    return (error: HttpErrorResponse): Observable<T> => {
      console.error(`❌ TypographyRepository: ${operation} failed:`, error);

      // Log detailed error information
      if (error.error instanceof ErrorEvent) {
        // Client-side error
        console.error(`❌ TypographyRepository: Client error: ${error.error.message}`);
      } else {
        // Server-side error
        console.error(`❌ TypographyRepository: Server error: ${error.status} - ${error.message}`);
        console.error(`❌ TypographyRepository: Error body:`, error.error);
      }

      // Return appropriate fallback
      return throwError(() => error);
    };
  }
}
